import React, { useCallback, useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { PiCaretDownFill, PiCaretUpFill, PiCheck } from 'react-icons/pi'
import { getDropdownZIndex } from 'styles/z-index'
import { useModalDepth } from 'components/UI/Modal/ModalPrimitive'
import { FormattedLabel } from 'components/Layout/shared'
import { cn } from 'components/UI/cn'
import type { BreadcrumbItem, BreadcrumbDropdownItem } from './types'

interface BreadcrumbsProps {
  items: BreadcrumbItem[]
  className?: string
}

const SECTION_LABEL_CLASSES =
  'font-inter text-xs font-normal tracking-wider text-nav-text-muted uppercase'

interface BreadcrumbDropdownProps {
  item: BreadcrumbItem
  isOpen: boolean
  onToggle: () => void
  onClose: () => void
}

interface BreadcrumbLinkProps {
  item: BreadcrumbItem
  isLast?: boolean
}

const BreadcrumbLink: React.FC<BreadcrumbLinkProps> = ({ item, isLast }) => {
  const navigate = useNavigate()

  const handleClick = useCallback(() => {
    if (item.path) {
      navigate(item.path)
    }
  }, [navigate, item.path])

  // Previous pages (not last) get underlined, current page (last) does not
  return (
    <button
      onClick={handleClick}
      className={cn(
        'font-inter text-sm text-sage-extra-lt bg-transparent border-none cursor-pointer',
        !isLast && 'underline'
      )}
    >
      {item.title && <span className="font-medium">{item.title}: </span>}
      <span className="font-light">{item.label}</span>
    </button>
  )
}

const BreadcrumbDropdown: React.FC<BreadcrumbDropdownProps> = ({
  item,
  isOpen,
  onToggle,
  onClose,
}) => {
  const navigate = useNavigate()
  const modalDepth = useModalDepth()
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const zIndex = React.useMemo(
    () => getDropdownZIndex(modalDepth),
    [modalDepth]
  )

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, onClose])

  const handleItemClick = useCallback(
    (dropdownItem: BreadcrumbDropdownItem) => {
      navigate(dropdownItem.path)
      onClose()
    },
    [navigate, onClose]
  )

  if (!item.hasDropdown || !item.dropdownItems?.length) {
    // Static text without dropdown - section labels are uppercase, entity labels are normal case
    if (item.isSectionLabel) {
      return <span className={SECTION_LABEL_CLASSES}>{item.label}</span>
    }
    // Entity label with title prefix
    return (
      <span className="font-inter text-sm text-sage-extra-lt">
        {item.title && <span className="font-medium">{item.title}: </span>}
        <span className="font-light">{item.label}</span>
      </span>
    )
  }

  return (
    <div className="relative flex items-center">
      <button
        ref={buttonRef}
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-haspopup="true"
        className={cn(
          'flex items-center gap-1 px-1.5 h-8 border-none font-inter text-sm font-medium rounded-lg cursor-pointer transition-all duration-150',
          isOpen
            ? 'text-white bg-brand-hover'
            : 'text-sage-extra-lt bg-transparent hover:bg-brand-hover'
        )}
      >
        <FormattedLabel label={item.label} variant="breadcrumb" />
        {isOpen ? <PiCaretUpFill size={14} /> : <PiCaretDownFill size={14} />}
      </button>

      {isOpen && (
        <div
          ref={dropdownRef}
          data-breadcrumb-menu
          className="absolute top-[calc(100%+4px)] left-0 min-w-[200px] max-h-[300px] overflow-y-auto bg-sage-lt rounded-lg shadow-[0px_4px_8px_3px_rgba(6,81,47,0.1)] py-1"
          style={{ zIndex }}
        >
          {item.dropdownItems.map((dropdownItem, index) => (
            <button
              key={index}
              onClick={() => handleItemClick(dropdownItem)}
              className={cn(
                'flex items-center justify-between w-[calc(100%-8px)] px-4 py-2 mx-1 border-none text-fg font-inter text-sm text-left cursor-pointer transition-colors duration-150 min-h-[32px] rounded',
                dropdownItem.isActive
                  ? 'bg-sage-md-dark font-semibold'
                  : 'bg-transparent font-normal hover:bg-sage-md'
              )}
            >
              <div className="flex flex-col gap-0.5">
                <FormattedLabel label={dropdownItem.label} variant="dropdown" />
                {dropdownItem.description && (
                  <span className="text-xs text-fg-secondary font-light">
                    {dropdownItem.description}
                  </span>
                )}
              </div>
              {dropdownItem.isActive && (
                <PiCheck size={16} className="text-fg flex-shrink-0" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  items,
  className,
}) => {
  const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(
    null
  )

  const handleToggle = useCallback((index: number) => {
    setOpenDropdownIndex((prev) => (prev === index ? null : index))
  }, [])

  const handleClose = useCallback(() => {
    setOpenDropdownIndex(null)
  }, [])

  if (!items.length) return null

  return (
    <nav
      className={cn('flex items-center h-8', className)}
      aria-label="Breadcrumb navigation"
    >
      {items.map((item, index) => (
        <React.Fragment key={index}>
          {index > 0 && (
            <span className="font-inter text-xs font-normal tracking-wider text-nav-text-muted mx-2">
              |
            </span>
          )}

          {item.hasDropdown ? (
            <BreadcrumbDropdown
              item={item}
              isOpen={openDropdownIndex === index}
              onToggle={() => handleToggle(index)}
              onClose={handleClose}
            />
          ) : item.isSectionLabel ? (
            item.path ? (
              <Link
                to={item.path}
                className={cn(SECTION_LABEL_CLASSES, 'underline')}
              >
                {item.label}
              </Link>
            ) : (
              <span className={SECTION_LABEL_CLASSES}>{item.label}</span>
            )
          ) : // Static text (for entity names) or clickable links
          item.path ? (
            <BreadcrumbLink item={item} isLast={index === items.length - 1} />
          ) : (
            <span className="font-inter text-sm text-sage-extra-lt">
              {item.title && (
                <span className="font-medium">{item.title}: </span>
              )}
              <span className="font-light">{item.label}</span>
            </span>
          )}
        </React.Fragment>
      ))}
    </nav>
  )
}
